import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BriefcaseBusiness,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Funnel,
  FolderOpen,
  RefreshCw,
  Search,
  Users,
  X,
} from 'lucide-react';
import api from '../../apiPm';
import AdvancedFilterPanel from '../../components/common/AdvancedFilter';
import {
  HEALTH_OPTIONS,
  PROJECT_STAGES,
  getSafeApiErrorMessage,
  getHealthTone,
  getStageTone,
  normalizeProject,
  withTimeout,
} from '../PmShared/projectUtils';
import './PmProjects.css';

const DEFAULT_FILTERS = {
  stage: '',
  health: '',
};

const formatUpdatedAt = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const KpiCard = ({ icon: Icon, tone, value, label, isLoading, isUnavailable }) => (
  <div className="col-6 col-xl">
    <div className={`card h-100 border-0 shadow-sm pm-project-kpi-card pm-project-kpi-${tone}`}>
      <div className="card-body d-flex align-items-center gap-3 py-3">
        <div className="pm-project-kpi-icon">
          <Icon size={20} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="text-muted text-truncate pm-project-kpi-label">{label}</div>
          <div className="fw-bold mt-1 pm-project-kpi-value">
            {isLoading ? <span className="placeholder col-5" /> : isUnavailable ? '-' : value}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ProjectsTableSkeleton = () => (
  <>
    {[1, 2, 3, 4].map((item) => (
      <tr key={item}>
        <td><span className="placeholder col-6" /></td>
        <td>
          <span className="placeholder col-8 mb-2" />
          <span className="placeholder col-5" />
        </td>
        <td><span className="placeholder col-7" /></td>
        <td><span className="placeholder col-5" /></td>
        <td><span className="placeholder col-6" /></td>
        <td><span className="placeholder col-8" /></td>
        <td><span className="placeholder col-7" /></td>
        <td><span className="placeholder col-6" /></td>
        <td><span className="placeholder col-6" /></td>
      </tr>
    ))}
  </>
);

// Stages PmDashboard's own `activeProjects` KPI counts as active — i.e.
// everything that isn't Completed or Blocked. Keep in sync with
// `activeProjects`/`completedProjects`/`blockedProjects` in the Go
// backend's domain/pm/service/service.go (DashboardSummary) so each KPI
// card's number and this filtered list always agree.
const ACTIVE_STAGES = ['Planning', 'In Progress', 'Review'];

// Keys a PM Dashboard KPI card can pass via `?stage=` — a macro layered on
// top of the regular single-select Stage filter below, since "Active" has
// no single matching PROJECT_STAGES value.
const DASHBOARD_STAGE_MACROS = {
  active: { label: 'Active', matches: (stage) => ACTIVE_STAGES.includes(stage) },
  completed: { label: 'Completed', matches: (stage) => stage === 'Completed' },
  blocked: { label: 'Blocked', matches: (stage) => stage === 'Blocked' },
};

const PmProjects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [query, setQuery] = useState('');
  const [filterData, setFilterData] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  // Set only when arriving via a dashboard KPI link (`?stage=active` /
  // `?stage=completed` / `?stage=blocked`) — a macro on top of the real
  // Stage filter above. Cleared by the dismiss button on the banner it
  // drives, or by picking a Stage from the regular Filter panel.
  const [dashboardStageMacro, setDashboardStageMacro] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stageParam = (params.get('stage') || '').toLowerCase();
    if (DASHBOARD_STAGE_MACROS[stageParam]) {
      setDashboardStageMacro(stageParam);
    }
    // Only meant to apply the incoming link once on arrival.
    // eslint-disable-next-line
  }, []);

  const clearDashboardStageMacro = () => {
    setDashboardStageMacro(null);
    setPage(1);
    navigate('/pm/projects', { replace: true });
  };

  const fetchProjects = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const response = await withTimeout(api.get('/v1/pm/crm-projects'));
      const payload = Array.isArray(response.data) ? response.data : response.data?.content || response.data?.data || [];
      setProjects(payload.map(normalizeProject));
    } catch (error) {
      setErrorMessage(getSafeApiErrorMessage(error, 'Unable to load CRM projects at the moment.'));
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProjects = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return projects.filter((project) => {
      const matchKeyword = !keyword ||
        project.title.toLowerCase().includes(keyword) ||
        project.clientName.toLowerCase().includes(keyword) ||
        project.owner.toLowerCase().includes(keyword);
      const matchHealth = !appliedFilters.health || project.health === appliedFilters.health;
      const matchStage = !appliedFilters.stage || project.stage === appliedFilters.stage;
      const macro = dashboardStageMacro && DASHBOARD_STAGE_MACROS[dashboardStageMacro];
      const matchDashboardStage = !macro || macro.matches(project.stage);
      return matchKeyword && matchHealth && matchStage && matchDashboardStage;
    });
  }, [projects, query, appliedFilters, dashboardStageMacro]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / rowsPerPage));
  const visibleProjects = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredProjects.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredProjects, page, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const activeFilterCount = useMemo(() => (
    Object.values(appliedFilters).filter(Boolean).length + (dashboardStageMacro ? 1 : 0)
  ), [appliedFilters, dashboardStageMacro]);

  const stats = useMemo(() => {
    const active = projects.filter((project) => ACTIVE_STAGES.includes(project.stage)).length;
    const atRisk = projects.filter((project) => ['At Risk', 'Delayed', 'Blocked'].includes(project.health) || project.stage === 'Blocked').length;
    const completed = projects.filter((project) => project.stage === 'Completed').length;
    const people = new Set(projects.flatMap((project) => project.members)).size;

    return {
      total: projects.length,
      active,
      atRisk,
      completed,
      people,
    };
  }, [projects]);

  const isDataUnavailable = Boolean(errorMessage) && projects.length === 0;
  const isFilterDisabled = isLoading || isDataUnavailable;
  const filterFields = useMemo(() => [
    {
      key: 'stage',
      label: 'Stage',
      type: 'select',
      placeholder: 'All stages',
      options: PROJECT_STAGES.map((stage) => ({ value: stage, label: stage })),
      disabled: isFilterDisabled,
    },
    {
      key: 'health',
      label: 'Project Health',
      type: 'select',
      placeholder: 'All health',
      options: HEALTH_OPTIONS.map((health) => ({ value: health, label: health })),
      disabled: isFilterDisabled,
    },
  ], [isFilterDisabled]);

  const handleFilterChange = (key, value) => {
    setFilterData((current) => ({ ...current, [key]: value || '' }));
  };

  const handleFilterApply = () => {
    setAppliedFilters(filterData);
    setPage(1);
    // A manually-picked Stage takes over from the dashboard macro rather
    // than stacking with it (e.g. macro=completed + Stage=Blocked would
    // otherwise always show zero rows).
    setDashboardStageMacro(null);
  };

  const handleFilterReset = () => {
    setFilterData(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const handleClearAll = () => {
    setQuery('');
    handleFilterReset();
    setDashboardStageMacro(null);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  return (
    <div className="pm-projects-page">
      <div className="page-header d-print-none">
        <div className="row align-items-center">
          <div className="col">
            <div className="page-pretitle">Project Management</div>
            <h2 className="page-title">
              <BriefcaseBusiness size={22} className="me-2 text-muted" />
              IT Audit Projects
            </h2>
            <div className="text-muted mt-1">
              Manage audit projects, owners, delivery stages, deadlines, and team assignments.
            </div>
          </div>
          <div className="col-auto ms-auto d-print-none">
            <div className="btn-list">
              <button className="btn btn-outline-primary d-inline-flex align-items-center" onClick={fetchProjects} disabled={isLoading}>
                <RefreshCw size={16} className={`me-2 ${isLoading ? 'pm-project-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        {errorMessage && (
          <div className="alert alert-warning pm-project-error-banner d-flex align-items-center" role="alert">
            <AlertTriangle size={18} className="me-3 flex-shrink-0" />
            <div className="flex-fill">
              <div className="fw-semibold">Project data unavailable</div>
              <div className="pm-project-helper">
                {errorMessage}
              </div>
            </div>
            <button className="btn btn-sm btn-outline-warning ms-3" onClick={fetchProjects}>Retry</button>
          </div>
        )}

        {dashboardStageMacro && (
          <div className="alert alert-info pm-project-dashboard-filter-banner d-flex align-items-center" role="status">
            <Funnel size={16} className="me-3 flex-shrink-0" />
            <div className="flex-fill">
              Filtered from dashboard: <strong>{DASHBOARD_STAGE_MACROS[dashboardStageMacro]?.label} projects</strong>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-3 d-inline-flex align-items-center"
              onClick={clearDashboardStageMacro}
            >
              <X size={14} className="me-1" />
              Clear
            </button>
          </div>
        )}

        <div className="row g-3 mb-3">
          <KpiCard icon={FolderOpen} tone="primary" value={stats.total} label="Total projects" isLoading={isLoading} isUnavailable={isDataUnavailable} />
          <KpiCard icon={BriefcaseBusiness} tone="success" value={stats.active} label="Active projects" isLoading={isLoading} isUnavailable={isDataUnavailable} />
          <KpiCard icon={AlertTriangle} tone="warning" value={stats.atRisk} label="At risk / blocked" isLoading={isLoading} isUnavailable={isDataUnavailable} />
          <KpiCard icon={CheckCircle2} tone="info" value={stats.completed} label="Completed" isLoading={isLoading} isUnavailable={isDataUnavailable} />
          <KpiCard icon={Users} tone="purple" value={stats.people} label="People involved" isLoading={isLoading} isUnavailable={isDataUnavailable} />
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-xl">
                <label className="form-label">Search Project</label>
                <div className="input-icon">
                  <span className="input-icon-addon">
                    <Search size={16} />
                  </span>
                  <input
                    className="form-control"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setPage(1);
                    }}
                    placeholder="Search project, client, or owner..."
                    disabled={isFilterDisabled}
                  />
                </div>
              </div>
              <div className="col-12 col-md-auto">
                <button
                  type="button"
                  className="btn btn-outline-secondary position-relative w-100"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasPmProjects"
                  disabled={isFilterDisabled}
                >
                  <Funnel size={16} className="me-1" />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className="badge bg-primary rounded-pill position-absolute pm-project-filter-badge">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
              <div className="col-12 col-md-auto">
                <button
                  className="btn btn-outline-secondary w-100"
                  disabled={isFilterDisabled}
                  onClick={handleClearAll}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <div className="card-title mb-0">Project List</div>
                {isDataUnavailable && <span className="badge bg-warning-lt">Data unavailable</span>}
              </div>
              <div className="card-subtitle text-muted">
                {isDataUnavailable
                  ? 'The project API could not be reached. Please retry after the connection is restored.'
                  : 'Track each IT Audit project from planning through final report.'}
              </div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-vcenter card-table">
              <thead>
                <tr>
                  <th className="pm-project-col-no">No.</th>
                  <th className="pm-project-col-project">Project</th>
                  <th className="pm-project-col-owner">Owner</th>
                  <th className="pm-project-col-pic">PIC</th>
                  <th className="pm-project-col-stage">Stage</th>
                  <th className="pm-project-col-progress">Progress</th>
                  <th className="pm-project-col-deadline">Next Deadline</th>
                  <th className="pm-project-col-health">Health</th>
                  <th className="pm-project-col-updated">Updated</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <ProjectsTableSkeleton />
                ) : filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan="9">
                      <div className="pm-project-table-state text-muted">
                        {errorMessage ? (
                          <AlertTriangle size={40} className="mb-2 opacity-25" />
                        ) : (
                          <FolderOpen size={40} className="mb-2 opacity-25" />
                        )}
                        <div className="fw-semibold">
                          {errorMessage ? 'Unable to load projects' : projects.length === 0 ? 'No CRM projects yet' : 'No projects found'}
                        </div>
                        <div className="pm-project-helper">
                          {errorMessage
                            ? 'Please retry or check the project API connection.'
                            : projects.length === 0
                              ? 'Projects created in CRM will appear here when available.'
                              : 'Try changing the search or filter criteria.'}
                        </div>
                        {errorMessage && (
                          <button className="btn btn-outline-primary btn-sm mt-3" onClick={fetchProjects}>Retry</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleProjects.map((project, index) => (
                    <tr key={project.id} className="pm-project-row-clickable" onClick={() => navigate(`/pm/projects/${project.id}`)}>
                      <td className="text-muted pm-project-col-no">{((page - 1) * rowsPerPage) + index + 1}</td>
                      <td>
                        <div className="fw-semibold text-primary">{project.title}</div>
                        <div className="text-muted pm-project-helper">{project.clientName}</div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="avatar avatar-sm rounded-circle">{project.owner.slice(0, 1).toUpperCase()}</span>
                          <span className="fw-semibold pm-project-person-name">{project.owner}</span>
                        </div>
                      </td>
                      <td>
                        <div className="pm-project-pic-cell">
                          {project.pic && project.pic !== '-' ? (
                            <>
                              <span className="avatar avatar-sm rounded-circle">{project.pic.slice(0, 1).toUpperCase()}</span>
                              <span className="fw-semibold">{project.pic}</span>
                            </>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge bg-${getStageTone(project.stage)}-lt`}>{project.stage}</span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="progress pm-project-progress">
                            <div className="progress-bar bg-primary" style={{ width: `${project.progress}%` }} />
                          </div>
                          <span className="text-muted pm-project-helper">{project.progress}%</span>
                        </div>
                      </td>
                      <td>{project.nextDeadline}</td>
                      <td>
                        <span className={`badge bg-${getHealthTone(project.health)}-lt`}>{project.health}</span>
                      </td>
                      <td className="text-muted">{formatUpdatedAt(project.updatedAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="card-footer pm-project-table-footer d-flex align-items-center">
            <p className="m-0 text-muted">
              {isDataUnavailable ? (
                <>Last refresh failed. Project records are not available.</>
              ) : filteredProjects.length === 0 ? (
                <>Showing <span>0</span> of <span>{projects.length}</span> projects</>
              ) : (
                <>Showing <span>{((page - 1) * rowsPerPage) + 1}</span>-<span>{Math.min(page * rowsPerPage, filteredProjects.length)}</span> of <span>{filteredProjects.length}</span> projects</>
              )}
            </p>
            {!isDataUnavailable && filteredProjects.length > 0 && (
              <div className="pm-project-pagination-controls ms-auto">
                <label className="pm-project-page-size">
                  Rows per page
                  <select
                    className="form-select form-select-sm"
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                  >
                    <option value={5}>5</option>
                    <option value={8}>8</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                  </select>
                </label>
                <div className="pm-project-pager" aria-label="Project list pagination">
                  <button
                    type="button"
                    className="btn btn-icon btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="pm-project-page-current">{page}</span>
                  <button
                    type="button"
                    className="btn btn-icon btn-sm"
                    disabled={page === totalPages}
                    onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                    aria-label="Next page"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <AdvancedFilterPanel
        offcanvasId="offcanvasPmProjects"
        title="Project Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={filterFields}
        showPeriod={false}
      />
    </div>
  );
};

export default PmProjects;
